/**
 * Get Started marketing page (`/getstarted`) — GitHub issue "Create React
 * components for static marketing pages using vite-react-ssg".
 *
 * Statically generated; no client-side data fetching or auth dependency
 * (Requirement #7) — the "Create Your Account" CTA is a plain
 * `react-router-dom` `<Link>` to `/signup` (the interactive, client-only
 * SPA route where account creation actually happens); this page itself
 * never calls Supabase or any API.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import SeoHead from '@/components/marketing/SeoHead';
import { Button } from '@/components/ui/button';

const STEP_KEYS = ['one', 'two', 'three'] as const;

export default function GetStartedPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <MarketingLayout>
      <SeoHead
        title={t('marketing.getStarted.title')}
        description={t('marketing.getStarted.description')}
        path="/getstarted"
      />

      <section className="container mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          {t('marketing.getStarted.heading')}
        </h1>
        <p className="mt-4 text-xl text-muted-foreground">{t('marketing.getStarted.subheading')}</p>

        <ol className="mx-auto mt-10 max-w-md space-y-4 text-left">
          {STEP_KEYS.map((key, index) => (
            <li key={key} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
              >
                {index + 1}
              </span>
              <span>{t(`marketing.getStarted.steps.${key}`)}</span>
            </li>
          ))}
        </ol>

        <div className="mt-10">
          <Button size="lg" asChild>
            <Link to="/signup">{t('marketing.getStarted.cta')}</Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
